/**
 * UUID / ID 生成工具
 * 用于生成 trackId 和 sessionId
 */

import { localStorageAdapter, sessionStorageAdapter } from './storage'

/**
 * 获取高精度时间戳（毫秒级，带小数精度）
 * 优先使用 performance.timeOrigin + performance.now()，回退到 Date.now()
 */
function getHighResTimestamp(): number {
  if (typeof performance !== 'undefined' && performance.timeOrigin && performance.now) {
    return Math.round(performance.timeOrigin + performance.now())
  }
  return Date.now()
}

/**
 * 自增序列号，确保同一运行时内 trackId 绝对唯一
 */
let trackIdSequence = 0

/**
 * 生成唯一 trackId（每条上报记录唯一）
 * 格式：时间戳_序列号_随机数_标识片段
 * 对齐文档：时间戳 + 随机数 + 设备/用户唯一标识片段
 */
export function generateTrackId(identifierFragment?: string): string {
  const timestamp = getHighResTimestamp()
  const seq = trackIdSequence++
  const random = Math.floor(Math.random() * 100000)
  const fragment = identifierFragment || 'fragment'
  return `${timestamp}_${seq}_${random}_${fragment}`
}

/**
 * 使用 UUID v4 格式（32 位）
 */
export function generateUuid(): string {
  // 优先使用原生 crypto.randomUUID
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // 降级方案：手动生成 UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * 生成设备唯一标识（持久化到 localStorage）
 */
export function generateDeviceUuid(): string {
  const STORAGE_KEY = 'xt_tracker_device_uuid'

  const stored = localStorageAdapter.getItem(STORAGE_KEY)
  if (stored) {
    return stored
  }

  const uuid = generateUuid()
  localStorageAdapter.setItem(STORAGE_KEY, uuid)
  return uuid
}

/**
 * 获取或创建会话 ID（会话级持久化）
 */
export function getOrCreateSessionId(): string {
  const STORAGE_KEY = 'xt_tracker_session_id'

  const stored = sessionStorageAdapter.getItem(STORAGE_KEY)
  if (stored) {
    return stored
  }

  const sessionId = generateUuid()
  sessionStorageAdapter.setItem(STORAGE_KEY, sessionId)
  return sessionId
}
